import React, { createContext, useContext, useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { I18N as I18N_DEFAULT } from "@/data/site";
import { useContent } from "@/lib/content";
import { LOCALES, DEFAULT_LOCALE, findLocaleByShort, findLocaleByPrefix, detectLocaleFromPath } from "@/lib/locales";

/**
 * mergeI18n — deep-merges the remote (DB) translation dictionary on top of the
 * built-in defaults, so a newly added key in `site.js` never renders empty
 * while the CMS document still lacks it. Remote non-empty strings win; empty
 * strings / undefined fall back to the default.
 */
function mergeI18n(defaults, remote) {
  if (!remote || typeof remote !== "object") return defaults;
  const out = {};
  for (const lang of Object.keys(defaults)) {
    out[lang] = mergeDeep(defaults[lang], remote[lang]);
  }
  return out;
}
function mergeDeep(a, b) {
  if (b === undefined || b === null) return a;
  if (typeof a !== "object" || Array.isArray(a)) return b === "" ? a : b;
  const out = { ...a };
  for (const k of Object.keys(b || {})) {
    out[k] = mergeDeep(a?.[k], b[k]);
  }
  return out;
}

const LangContext = createContext({
  lang: "pt",
  locale: DEFAULT_LOCALE,        // full locale object (with market, prefix, htmlLang, etc.)
  setLang: () => {},
  setLocale: () => {},
  t: I18N_DEFAULT.pt,
});

/**
 * LangProvider — augmented to be locale-aware while preserving the legacy
 * short-code API (`lang` / `setLang`). New callers can use `locale` and
 * `setLocale` for the full BCP-47 identifier.
 *
 * The provider watches the URL prefix (/pt-br/, /pt-pt/, /es-py/) and syncs
 * the language state accordingly — so a visitor landing directly on a
 * regional URL sees the correct interface without extra state juggling.
 */
export function LangProvider({ children }) {
  const location = useLocation();
  // Priority order: URL prefix → localStorage → default (pt-BR)
  const urlLocale = detectLocaleFromPath(location.pathname);
  const stored = typeof window !== "undefined" ? localStorage.getItem("places_locale") : null;
  const initial = urlLocale || findLocaleByPrefix(stored) || DEFAULT_LOCALE;

  const [locale, setLocaleState] = useState(initial);
  const lang = locale.short; // legacy short code, still exposed unchanged
  const setLang = (short) => setLocaleState(findLocaleByShort(short));
  const setLocale = (l) => setLocaleState(typeof l === "string" ? (findLocaleByPrefix(l) || DEFAULT_LOCALE) : l);

  // Whenever the URL changes to a different regional prefix, keep context in sync.
  useEffect(() => {
    const detected = detectLocaleFromPath(location.pathname);
    if (detected && detected.code !== locale.code) setLocaleState(detected);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Persist choice + reflect in <html lang="...">
  useEffect(() => {
    try { localStorage.setItem("places_locale", locale.prefix); } catch { /* ignore */ }
    if (typeof document !== "undefined") document.documentElement.lang = locale.htmlLang;
  }, [locale]);

  const { i18n } = useContent();
  // Deep-merge remote i18n on top of the built-in defaults. This guarantees
  // that newly introduced keys (added in code) always fall back to their PT
  // default even before the CMS document is re-saved.
  const I18N = mergeI18n(I18N_DEFAULT, i18n);
  const t = I18N[lang] || I18N_DEFAULT.pt;

  return (
    <LangContext.Provider value={{ lang, locale, setLang, setLocale, t }}>
      {children}
    </LangContext.Provider>
  );
}

export const useLang = () => useContext(LangContext);
export { LOCALES };
